import { Prompt } from '@/components/Prompt'
import { getWorkflowSpecifications } from '@/defaults/workflowSpecification'
import { useNavigationKeys } from '@/hooks/useNavigationKeys'
import { type YDocument } from '@/modules/yjs/hooks'
import { useWorkflowStatus } from '@/hooks/useWorkflowStatus'
import { Button } from '@ttab/elephant-ui'
import type { LucideIcon } from '@ttab/elephant-ui/icons'
import { XIcon } from '@ttab/elephant-ui/icons'
import { useState, type JSX } from 'react'
import type * as Y from 'yjs'
import { snapshotDocument } from '@/lib/snapshotDocument'
import { toast } from 'sonner'
import { useTranslation } from 'react-i18next'

export const ViewDialogClose = ({ ydoc, onClick, Icon = XIcon, asDialog }: {
  ydoc?: YDocument<Y.Map<unknown>>
  Icon?: LucideIcon
  onClick: () => void
  asDialog?: boolean
}): JSX.Element => {
  const [showVerifyDialog, setShowVerifyDialog] = useState(false)
  const [documentStatus] = useWorkflowStatus({ documentId: ydoc?.id })
  const root = ydoc?.ele.get('root') as Y.Map<unknown>
  const documentType = root?.get('type')


  const { t } = useTranslation('shared')
  const asSave = (documentStatus?.type
    ? getWorkflowSpecifications()[documentStatus.type][documentStatus.name].asSave && ydoc?.isChanged
    : false) || false

  const handleClose = () => {
    if (asSave) {
      setShowVerifyDialog(true)
    } else {
      onClick()
    }
  }

  useNavigationKeys({
    stopPropagation: true,
    keys: ['Escape'],
    onNavigation: () => {
      if (asDialog) {
        handleClose()
      } else {
        setShowVerifyDialog(false)
      }
    }
  })

  return (
    <>
      <Button
        variant='ghost'
        onClick={handleClose}
        className='w-9 h-9 p-0 hover:bg-gray-200 dark:hover:bg-table-focused'
      >
        <Icon size={18} strokeWidth={1.75} />
      </Button>
      {showVerifyDialog && (
        <Prompt
          title={documentType === 'core/factbox' ? t('factbox:prompts.approveBeforeClose') : t('prompts.publishBeforeClose')}
          description={documentType === 'core/factbox' ? t('factbox:prompts.savedButNotApproved') : t('prompts.savedButNotPublished')}
          onPrimary={() => {
            if (ydoc) {
              snapshotDocument(ydoc?.id, {
                status: 'usable'
              }, ydoc.provider?.document)
                .then(() => {
                  setShowVerifyDialog(false)
                  onClick()
                })
                .catch((error) => {
                  setShowVerifyDialog(false)
                  toast.error(t('errors:toasts.savedDocumentFailed'))
                  console.error('Could not snapshot document before closing view.', error)
                })
            }
          }}
          primaryLabel={t('common:actions.yes')}
          onSecondary={() => {
            setShowVerifyDialog(false)
            onClick()
          }}
          secondaryLabel={t('common:actions.no')}
          onCancel={() => setShowVerifyDialog(false)}
          cancelLabel={t('common:actions.abort')}
        />
      )}
    </>
  )
}
